/**
 * Fusion d'une baseline réelle partielle (data/processed/baseline_2026.json, `meta.partial`) par-dessus une baseline de
 * secours complète (fixture, chiffres `source: "TODO"`). ADR 0004 §5.
 *
 * Règles : un chiffre réel (`value` non null) remplace le chiffre de secours ; un chiffre réel `null` ou absent laisse le
 * chiffre de secours **avec sa source `TODO`** (l'UI affiche « non sourcé ») ; un tableau réel non vide remplace le tableau
 * de secours en bloc (missions, recettes, trajectoires) ; `meta` = celle de la baseline réelle, millésime annoté.
 * Fonction pure, sans I/O. Le moteur lui-même continue de refuser toute baseline contenant un `null`
 * (`assertBaselineComplete`) : la fusion est à faire AVANT `simulate`, par l'appelant (UI, scripts).
 */
import type { Baseline, Figure } from "./types.js";

export interface MergedBaseline {
  baseline: Baseline;
  /** Chemins (`macro.potential_growth`, `apu.apparent_rate`…) servis par la baseline de secours, donc non sourcés. */
  fallback_paths: string[];
  /** Chemins servis par la baseline réelle. */
  real_paths: string[];
}

function isFigure(x: unknown): x is Figure {
  return !!x && typeof x === "object" && "value" in x && "unit" in x && "source" in x;
}

function mergeNode(fallback: unknown, real: unknown, path: string, out: MergedBaseline): unknown {
  if (isFigure(fallback) || isFigure(real)) {
    if (isFigure(real) && real.value !== null) {
      out.real_paths.push(path);
      return real;
    }
    if (isFigure(fallback)) {
      if (fallback.value !== null) out.fallback_paths.push(path);
      return fallback;
    }
    return real;
  }
  if (real === null || real === undefined) return fallback;
  if (Array.isArray(real)) {
    if (real.length > 0) {
      out.real_paths.push(path);
      return real;
    }
    if (Array.isArray(fallback) && fallback.length > 0) out.fallback_paths.push(path);
    return fallback;
  }
  if (typeof real === "object") {
    const fb = fallback && typeof fallback === "object" && !Array.isArray(fallback) ? (fallback as Record<string, unknown>) : {};
    const merged: Record<string, unknown> = { ...fb };
    const keys = new Set([...Object.keys(fb), ...Object.keys(real as Record<string, unknown>)]);
    for (const k of keys) merged[k] = mergeNode(fb[k], (real as Record<string, unknown>)[k], path ? `${path}.${k}` : k, out);
    return merged;
  }
  return real;
}

/**
 * Bascule v0.3 (ADR 0014 §3) : si la baseline porte les champs du constructeur de données v0.3, ils deviennent les champs
 * consommés — `state.missions` ← `missions_v03` (sinon agrégation de `lolf_missions` par `mission_groups.map`), `state.revenues` ←
 * `revenues_v03`, `asso.trend` / `apul.trend` ← `trend_v03`. Les champs `*_v03` restent présents (traçabilité). Pure, idempotente.
 */
export function promoteV03(b: Baseline): { baseline: Baseline; promoted: string[] } {
  const promoted: string[] = [];
  const st = { ...b.state };
  const groups = st.missions_v03 && st.missions_v03.length > 0 ? st.missions_v03 : aggregateLolf(b);
  if (groups && st.missions !== groups) {
    st.missions = groups;
    promoted.push("state.missions");
  }
  if (st.revenues_v03 && st.revenues_v03.length > 0 && st.revenues !== st.revenues_v03) {
    st.revenues = st.revenues_v03;
    promoted.push("state.revenues");
  }
  const sector = (x: Baseline["asso"], name: string): Baseline["asso"] => {
    if (x.trend_v03 && x.trend_v03.length > 0 && x.trend !== x.trend_v03) {
      promoted.push(`${name}.trend`);
      return { ...x, trend: x.trend_v03 };
    }
    return x;
  };
  const asso = sector(b.asso, "asso");
  const apul = sector(b.apul, "apul");
  if (promoted.length === 0) return { baseline: b, promoted };
  return { baseline: { ...b, state: st, asso, apul }, promoted };
}

/**
 * Groupes du jeu agrégés depuis les missions LOLF officielles (`state.lolf_missions` × `mission_groups.map`) ; null si l'une des
 * deux manque. Une mission LOLF absente de la table (ex. « Engagements financiers de l'État », charge de la dette) est exclue.
 */
export function aggregateLolf(b: Baseline): Baseline["state"]["missions"] | null {
  const lolf = b.state.lolf_missions;
  const map = b.mission_groups?.map;
  if (!lolf || lolf.length === 0 || !map) return null;
  const out = new Map<string, number>();
  for (const m of lolf) {
    const g = map[m.id];
    if (!g || m.credits.value === null) continue;
    out.set(g, (out.get(g) ?? 0) + m.credits.value);
  }
  return [...out.entries()].map(([id, v]) => ({ id, label: id, credits: { value: v, unit: "bn_eur", source: "TODO", note: "Σ des missions LOLF du groupe (calcul)" } }));
}

/** Fusionne `real` (partielle) par-dessus `fallback` (complète), puis applique la bascule v0.3. Ne modifie aucun des deux arguments. */
export function mergeBaseline(fallback: Baseline, real: Baseline): MergedBaseline {
  const out: MergedBaseline = { baseline: fallback, fallback_paths: [], real_paths: [] };
  const { meta: fbMeta, ...fbRest } = fallback;
  const { meta: realMeta, ...realRest } = real;
  const merged = mergeNode(fbRest, realRest, "", out) as Omit<Baseline, "meta">;
  out.baseline = {
    ...merged,
    meta: {
      ...realMeta,
      vintage: out.fallback_paths.length > 0 ? `${realMeta.vintage} ; champs manquants servis par « ${fbMeta.vintage} » (non sourcés)` : realMeta.vintage,
    },
  };
  // Bascule v0.3 : les champs consommés servis par la fixture sont remplacés par les données réelles v0.3.
  const pr = promoteV03(out.baseline);
  out.baseline = pr.baseline;
  out.fallback_paths = out.fallback_paths.filter((p) => !pr.promoted.includes(p));
  for (const p of pr.promoted) if (!out.real_paths.includes(p)) out.real_paths.push(p);
  out.fallback_paths.sort();
  out.real_paths.sort();
  return out;
}

/** Vrai si un chiffre doit être affiché « non sourcé » : source `TODO` (chiffre de secours ou non relevé). */
export function isUnsourced(fig: Figure | undefined): boolean {
  return fig === undefined || fig.source === "TODO";
}
